import { canonicalFeedbackPreviewEnabled } from '@/features/feedback-integration/feedbackPreview';
import { useCanonicalCoachWorkspace } from '@/features/feedback-integration/CanonicalCoachWorkspaceHost';
import { feedbackNavigationSearch } from '@/lib/feedbackNavigation';
import type { FeedbackConversationTarget } from '@/lib/types';
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import {
  ALL_COACH_TABS,
  OPERATIONAL_COACH_TABS,
  type CoachTab,
} from "./CoachTabStrip";
import { canAuthorCoachFeedback } from "./coachWorkspaceAccess";
import { CoachFeedbackBoardPage } from "./CoachFeedbackBoardPage";
import { RosterPage } from "./RosterPage";
import { FeedbackLibraryPage } from "./FeedbackLibraryPage";
import { SettingsPage } from "./SettingsPage";
import { CoachMatchesPage } from "./CoachMatchesPage";
import {
  GoalComposerModal,
  type GoalComposerSource,
} from "./components/GoalComposerModal";
import { CoachDevelopmentModal } from "./components/CoachDevelopmentModal";
import { QuickFeedbackModal } from "./components/QuickFeedbackModal";
import {
  saveDraft,
  useComposerDraftContext,
  draftContextKey,
  isCurrentDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { useCoachGoalMutations } from "./hooks/useCoachGoals";
import type { GoalFeedbackEntry } from './hooks/useGoalFromFeedback';
import {
  WorkspaceHeader,
  WorkspaceHeaderProvider,
} from "./components/WorkspaceHeader";
import { PageHeader } from "./components/PageHeader";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import { useScopeStore } from "@/stores/scope";
import type { PlayerGoal } from "@/lib/types";

/**
 * Coach workspace tabbed shell — the host for the `/coach` list
 * pages (Board · Players · Matches · Feedback Library · Settings).
 * Ports the legacy `setCoachTab` /
 * `renderCoachWorkspace` shell from `js/coaching.js`.
 *
 * Routing: `/coach?tab=<tab>`. The `tab` search param selects an
 * object/list page; absent ⇒ Board (Phase 1 player-first landing).
 * `/coach/review` is a
 * SEPARATE route (the M4/M5 surface) — the "Review" tab in the
 * strip links there rather than rendering under `?tab=review`, so
 * Coach Review's own `match_id`/`note_id`/`clip_id` schema and the
 * 40 passing M4/M5 E2E tests are untouched.
 *
 * `useMe()` reconciles the auth + scope stores on a direct nav —
 * the list-page queries (Roster/Notes/etc.) are scope-gated, same
 * as Coach Review needs it (the M5 latent-bug fix).
 *
 * M6a slice 1 ships the shell + routing + tab strip. The list
 * pages themselves are placeholders here and get built in the
 * following M6a slices — each one swaps its placeholder for the
 * real surface.
 */

/** The tabs the SHELL hosts — Review is its own route, so it's not
 * in this set even though it's in the strip. */
type ShellTab = Exclude<CoachTab, "review">;

const SHELL_TAB_LABELS: Record<ShellTab, string> = {
  board: "Team Feedback Board",
  roster: "Players",
  matches: "Matches",
  library: "Feedback Library",
  settings: "Settings",
};

export function CoachShell() {
  // Reconcile auth + scope stores — the list-page queries are
  // scope-gated, and a direct nav to /coach must not leave them
  // unreconciled (the M5 CoachReview latent-bug pattern).
  const me = useMe();
  const navigate = useNavigate({ from: "/coach" });

  // Auth gate. An explicit 401 from /api/me ⇒ redirect to
  // `/login?next=<current path>`. A
  // `useRef` flag prevents the effect from re-firing after the
  // redirect URL change races with the in-flight /api/me resolution.
  //
  // `next` captures the FULL path + search (`/coach?tab=clips`) at
  // mount via a ref so a viewer deep-linked to a specific tab lands
  // back on that tab after signing in, not the roster default. The
  // ref freezes the value before the redirect rewrites the URL.
  // `window.location` is the same source `PublicAppHeader` uses to
  // build its `loginNext`.
  const isAuthError = me.error instanceof ApiError && me.error.status === 401;
  const isAnon = me.data === undefined ? false : me.data?.user == null;
  const showLogin = isAuthError || isAnon;
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/coach"
      : "/coach",
  );
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate]);

  // Wrong-role gate. A signed-in user with no coach access (a plain
  // player / guardian, an uploader-only admin) who reaches /coach via
  // a stale bookmark or a hand-typed URL gets bounced to `/hub`, the
  // role-based redirect, so they land on their OWN home instead of a
  // shell whose scope-gated queries would just 403. This mirrors the
  // exact `canOpenCoach` rule `PublicAppHeader` uses to show/hide the
  // Coach link — the gate and the nav stay in lock-step. Anonymous
  // users are handled by the auth gate above (this effect waits for
  // `me.data?.user` to resolve), so the two never fight.
  const canOpenCoach = getPublicNavCapabilities(me.data).canOpenCoach;
  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!me.data?.user) return;
    if (canOpenCoach) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({ to: "/hub", replace: true });
  }, [showLogin, me.data, canOpenCoach, navigate]);

  const search = useSearch({ from: "/coach" });
  const canonical = canonicalFeedbackPreviewEnabled();
  const canonicalWorkspace=useCanonicalCoachWorkspace();
  const authoringHost=canonicalWorkspace?.authoring;
  const openCanonical = (target: FeedbackConversationTarget) => void navigate({search: previous => ({...previous,...feedbackNavigationSearch(target),recap_id:undefined})});
  const canUseFeedbackSurfaces = me.data?.user
    ? canAuthorCoachFeedback(me.data)
    : true;
  const visibleTabs = canUseFeedbackSurfaces
    ? ALL_COACH_TABS
    : OPERATIONAL_COACH_TABS;
  const requestedTab: ShellTab = normalizeShellTab(search.tab);
  const requestedFeedbackTab =
    requestedTab === "board" || requestedTab === "library";
  const activeTab: ShellTab =
    canUseFeedbackSurfaces || !requestedFeedbackTab ? requestedTab : "matches";
  const tabRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin || !me.data?.user || !canOpenCoach) return;
    if (canUseFeedbackSurfaces) return;
    if (!requestedFeedbackTab) return;
    if (tabRedirectFired.current) return;
    tabRedirectFired.current = true;
    void navigate({
      to: "/coach",
      search: { tab: "matches" },
      replace: true,
    });
  }, [
    showLogin,
    me.data,
    canOpenCoach,
    canUseFeedbackSurfaces,
    requestedFeedbackTab,
    requestedTab,
    navigate,
  ]);

  // Multi-team users who haven't picked a team yet must do so before
  // any scope-gated tab renders — otherwise the coach surfaces query
  // against a stale/empty scope.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  // A2: scopes the composer draft saved on Quick-Feedback escalation,
  // matching the key Coach Review's mount-seed reads.
  const draftContext = useComposerDraftContext();

  // The goal-composer modal + the Player Development modal are
  // both owned by the SHELL, not a list page — holding their
  // state here keeps "one modal at a time" structural (the
  // `GoalComposerModal` and `CoachDevelopmentModal` both wrap
  // `CoachModal`, so two open at once would both grab Escape).
  //
  // `goalModal`:
  //   - `null`                       → closed
  //   - `{ mode: "create", playerId}`→ create a goal for a player
  //   - `{ mode: "edit", goal }`     → edit an existing goal
  // `devModalPlayer`: the player whose Development modal is open.
  // Opening the goal composer from the dev modal temporarily closes the plan
  // (one portal at a time) and records enough context to restore it.
  const goalMutations = useCoachGoalMutations();
  type PlayerPlanContext = { id: string; name: string };
  const playerPlanReturnFocus = useRef<HTMLElement | null>(null);
  const [goalModal, setGoalModal] = useState<
    | {
        mode: "create";
        playerId: string;
        /** M7.5 D4: optional source evidence — set when the modal
         * opens from a Notes / Clips list row's "+ New goal"
         * button. Plumbs `source_note_id` / `source_clip_id` +
         * seed title/description through `<GoalComposerModal>`. */
        source?: GoalComposerSource;
        feedbackSource?: GoalFeedbackEntry;
        returnToPlan?: PlayerPlanContext;
      }
    | {
        mode: "edit";
        goal: PlayerGoal;
        returnToPlan?: PlayerPlanContext;
      }
    | null
  >(null);
  const [devModalPlayer, setDevModalPlayer] =
    useState<PlayerPlanContext | null>(null);
  // Each opening owns its close callback, including after an async save.
  // Scope equality alone cannot distinguish two openings for the same author.
  const quickFeedbackSequence = useRef(0);
  const [quickFeedbackSession, setQuickFeedbackSession] = useState<{
    id: number;
    context: DraftContext;
    playerId?: string;
  } | null>(null);
  const quickFeedbackOpen = !!quickFeedbackSession &&
    draftContextKey(quickFeedbackSession.context) === draftContextKey(draftContext);
  const closeQuickFeedback = (session: typeof quickFeedbackSession) => {
    setQuickFeedbackSession((current) => current === session ? null : current);
  };
  useEffect(() => {
    if (quickFeedbackSession && !isCurrentDraftContext(quickFeedbackSession.context)) {
      closeQuickFeedback(quickFeedbackSession);
    }
  }, [draftContext, quickFeedbackSession]);
  const openQuickFeedback = (playerId?: string) => {
    if (canonical) { authoringHost?.current?.open(playerId); return; }
    if (draftContext) {
      setQuickFeedbackSession({
        id: ++quickFeedbackSequence.current,
        context: draftContext,
        playerId,
      });
    }
  };

  if (showLogin) {
    return (
      <main
        className="min-h-screen p-6 text-ink"
        data-testid="coach-shell-auth-redirect"
      />
    );
  }

  // Signed-in but no coach access — the wrong-role effect above is
  // mid-redirect to `/hub`. Render an empty shell so the scope-gated
  // coach tabs never mount (and never fire queries that would 403)
  // for the frame before the navigate lands.
  if (me.data?.user && !canOpenCoach) {
    return (
      <main
        className="min-h-screen p-6 text-ink"
        data-testid="coach-shell-role-redirect"
      />
    );
  }

  return (
    <WorkspaceHeaderProvider>
      <main className="min-h-screen p-3 text-ink sm:p-5">
        <WorkspaceHeader active={activeTab} visibleTabs={visibleTabs} />

        {selectionRequired ? (
          <>
            <PageHeader title={SHELL_TAB_LABELS[activeTab]} description="Choose your team to open the workspace." />
            <TeamScopeGate />
          </>
        ) : (
          <section
            aria-label={SHELL_TAB_LABELS[activeTab]}
            data-testid={`coach-shell-panel-${activeTab}`}
          >
            {/* Every coach tab is now a real surface — the placeholder
             * fallback only fires for an unreachable tab value. */}
            {activeTab === "board" ? (
              <CoachFeedbackBoardPage onOpenFeedback={canonical ? openCanonical : undefined} onGiveFeedback={openQuickFeedback} authoringReady={!!draftContext} />
            ) : activeTab === "roster" ? (
              <RosterPage
                onOpenFeedback={canonical ? openCanonical : undefined}
                canUseFeedbackSurfaces={canUseFeedbackSurfaces}
                onGiveFeedback={openQuickFeedback}
                onCreatePlayerGoal={(playerId) =>
                  setGoalModal({ mode: "create", playerId })
                }
                onOpenPlayerGoal={(goal) =>
                  setGoalModal({ mode: "edit", goal })
                }
                onOpenDevelopment={(id, name) => {
                  playerPlanReturnFocus.current =
                    document.activeElement instanceof HTMLElement
                      ? document.activeElement
                      : null;
                  setDevModalPlayer({ id, name });
                }}
              />
            ) : activeTab === "library" ? (
              // M7.5 D1: "+ New goal" on Notes rows opens the goal
              // composer with the note/clip as source evidence.
              <FeedbackLibraryPage
                onOpenFeedback={canonical ? openCanonical : undefined}
                onResumeFeedback={id=>authoringHost?.current?.resume(id)}
                onGiveFeedback={()=>openQuickFeedback()}
                authoringReady={!!draftContext}
                onCreateFeedbackGoal={canonical && draftContext ? feedbackId => {
                  if (!isCurrentDraftContext(draftContext)) return;
                  const { actorId, clubId, teamId, seasonId } = draftContext;
                  setGoalModal({ mode: 'create', playerId: '', feedbackSource: {
                    entryId: crypto.randomUUID(), feedbackId, scope: { actorId, clubId, teamId, seasonId },
                  } });
                } : undefined}
                onCreatePlayerGoal={(playerId, source) =>
                  setGoalModal({ mode: "create", playerId, source })
                }
              />
            ) : activeTab === "matches" ? (
              <CoachMatchesPage />
            ) : activeTab === "settings" ? (
              <SettingsPage />
            ) : (
              <CoachTabPlaceholder tab={activeTab} />
            )}
          </section>
        )}

        {/* Player Development modal — opened from a Roster row's
         * "Development profile" button. Its goal-composer entry
         * points close THIS modal and open the composer (one modal
         * at a time). */}
        {devModalPlayer ? (
          <CoachDevelopmentModal
            playerId={devModalPlayer.id}
            playerName={devModalPlayer.name}
            onClose={() => setDevModalPlayer(null)}
            returnFocusTo={playerPlanReturnFocus.current}
            onNewGoal={(playerId) => {
              const returnToPlan = devModalPlayer;
              setDevModalPlayer(null);
              setGoalModal({ mode: "create", playerId, returnToPlan });
            }}
            onEditGoal={(goal) => {
              const returnToPlan = devModalPlayer;
              setDevModalPlayer(null);
              setGoalModal({ mode: "edit", goal, returnToPlan });
            }}
          />
        ) : null}

        {/* Goal-composer modal — create (from a Roster row or the
         * dev modal) or edit (from the dev modal's goal cards). */}
        {goalModal ? (
          <GoalComposerModal
            key={goalModal.mode === 'create' ? goalModal.feedbackSource?.entryId : undefined}
            goal={goalModal.mode === "edit" ? goalModal.goal : null}
            preselectedPlayerId={
              goalModal.mode === "create" ? goalModal.playerId : undefined
            }
            source={goalModal.mode === "create" ? goalModal.source : undefined}
            feedbackSource={goalModal.mode === 'create' ? goalModal.feedbackSource : undefined}
            mutations={goalMutations}
            returnFocusTo={
              goalModal.returnToPlan ? playerPlanReturnFocus.current : undefined
            }
            onClose={() => {
              // A late callback from a prior source opening cannot close a new one.
              if (goalModal.mode === 'create' && goalModal.feedbackSource) {
                setGoalModal(current => current === goalModal ? null : current); return;
              }
              const returnToPlan = goalModal.returnToPlan;
              setGoalModal(null);
              if (returnToPlan) setDevModalPlayer(returnToPlan);
            }}
          />
        ) : null}

        {canonical && <CanonicalOverlaySuspension active={!!goalModal||!!devModalPlayer||selectionRequired||!canUseFeedbackSurfaces}/>}

        {quickFeedbackOpen && quickFeedbackSession ? (
          <QuickFeedbackModal
            key={quickFeedbackSession.id}
            initialPlayerId={quickFeedbackSession.playerId}
            onClose={() => closeQuickFeedback(quickFeedbackSession)}
            onOpenCoachReview={({ playerId, matchId, draft }) => {
              if (!isCurrentDraftContext(quickFeedbackSession.context)) return;
              closeQuickFeedback(quickFeedbackSession);
              // A2: persist the typed feedback as a create-mode note
              // draft so Coach Review's mount-seed (A1) restores it —
              // the coach re-types nothing. Keep the modal's captured owner.
              if (draft) {
                saveDraft(
                  { ...quickFeedbackSession.context, kind: "note" },
                  draft,
                );
              }
              void navigate({
                to: "/coach/review",
                search: {
                  source: "video",
                  ...(playerId ? { player_id: playerId } : {}),
                  ...(matchId ? { match_id: matchId } : {}),
                },
              });
            }}
          />
        ) : null}
      </main>
    </WorkspaceHeaderProvider>
  );
}

function normalizeShellTab(tab: string | undefined): ShellTab {
  if (tab === "notes" || tab === "clips" || tab === "summaries") {
    return "library";
  }
  if (
    tab === "roster" ||
    tab === "matches" ||
    tab === "library" ||
    tab === "settings"
  ) {
    return tab;
  }
  return "board";
}

/** Temporary per-tab placeholder — replaced surface-by-surface in
 * the M6a slices that follow the shell. */
function CoachTabPlaceholder({ tab }: { tab: ShellTab }) {
  return (
    <div
      className="rounded border border-rule bg-elevated px-6 py-12 text-center"
      data-testid="coach-shell-placeholder"
    >
      <p className="text-ink font-display text-lg">{SHELL_TAB_LABELS[tab]}</p>
      <p className="text-ink-muted text-sm mt-1">
        This surface lands in an upcoming M6a slice.
      </p>
    </div>
  );
}

/** Local preview dependencies stay outside the production shell lifetime. */
function CanonicalOverlaySuspension({active}:{active:boolean}) {
  const setOtherOverlay=useCanonicalCoachWorkspace()?.setOtherOverlay;
  useEffect(()=>{setOtherOverlay?.(active);return()=>setOtherOverlay?.(false);},[active,setOtherOverlay]);
  return null;
}
